import { isPlatformBrowser } from '@angular/common';
import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import { Router } from '@angular/router';
import { DEALER_AUTH_COOKIE_NAME, getImprintUrl } from '@hyundai/shared-lib';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, from, map, of, switchMap, tap } from 'rxjs';
import { UserApiService } from '../../api/user-api.service';
import { isHWS } from '../../providers/platform.token';
import { injectVehicleMarketUrl } from '../../providers/vehicle-market-url.token';
import { CookieService } from '../../services/cookie.service';
import { injectRequestContext, setRedirect } from '../../utilities/redirect';
import { actions } from './login.actions';

@Injectable()
export class LoginEffects {
    protected readonly actions$ = inject(Actions);
    protected readonly store = inject(Store);
    protected readonly userApi = inject(UserApiService);
    protected readonly cookieService = inject(CookieService);
    protected readonly router = inject(Router);
    protected readonly platformId = inject(PLATFORM_ID);
    protected readonly vehicleMarketUrl = injectVehicleMarketUrl();
    protected readonly isHWS = isHWS();
    protected readonly reqContext = injectRequestContext();
    constructor() {
        const dealerAuthToken = this.cookieService.get(DEALER_AUTH_COOKIE_NAME);
        if (dealerAuthToken) {
            if (isPlatformBrowser(this.platformId)) {
                setTimeout(() => {
                    this.store.dispatch(actions.oAuthTokenSuccess({ token: dealerAuthToken }));
                }, 0);
            } else {
                this.store.dispatch(actions.oAuthTokenSuccess({ token: dealerAuthToken }));
            }
        }
    }

    startLogin$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(actions.loginStart),
            switchMap(({ email }) =>
                from(this.userApi.sendLoginCode(email)).pipe(
                    map(() => actions.loginStartSuccess()),
                    catchError((error) => of(actions.loginStartFailure({ error }))),
                ),
            ),
        );
    });

    oAuthToken$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(actions.oAuthToken),
            switchMap(({ email, code }) =>
                from(this.userApi.verifyLoginCode(email, code)).pipe(
                    map((res) => {
                        this.cookieService.set(DEALER_AUTH_COOKIE_NAME, res.token, 1, '/', true);
                        return res;
                    }),
                    map((res) => actions.oAuthTokenSuccess({ token: res.token, redirect: true })),
                    catchError((error) => of(actions.oAuthTokenFailure({ error }))),
                ),
            ),
        );
    });

    initHygraphForm$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(actions.initHygraphForm),
            switchMap((action) => [actions.getFormId({ name: action.name })]),
        );
    });

    getUserInfo$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(actions.oAuthTokenSuccess, actions.loadUserInfo),
            switchMap(() =>
                from(this.userApi.getUserInfo()).pipe(
                    map((res) => actions.loadUserInfoSuccess({ userInfo: res })),
                    catchError((error) => [
                        actions.loadUserInfoFailure({ error }),
                        actions.oAuthTokenFailure({ error }),
                        actions.logout(),
                    ]),
                ),
            ),
        );
    });

    getFormId$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(actions.getFormId),
            switchMap(({ name }) =>
                from(this.userApi.getFormId(name)).pipe(
                    map((res) => actions.getFormIdSuccess({ formId: res.formId })),
                    catchError((error) => of(actions.getFormIdFailure({ error }))),
                ),
            ),
        );
    });

    logout$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(actions.logout),
            map(() => {
                this.cookieService.delete(DEALER_AUTH_COOKIE_NAME);
                return actions.logoutSuccess();
            }),
        );
    });

    onTokenSuccessRedirect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(actions.oAuthTokenSuccess),
            switchMap((action) => {
                if (!action.redirect) {
                    return [];
                }
                return [actions.tryRedirectToPartnerWeb()];
            }),
        );
    });

    tryRedirectToPartnerWeb$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(actions.tryRedirectToPartnerWeb),
                tap(async () => {
                    try {
                        const res = await this.userApi.getDealerSlug();
                        if (!res.hasHws && this.isHWS) {
                            const url = `${this.vehicleMarketUrl}/login`;
                            if (this.reqContext) {
                                setRedirect(this.reqContext, 307, url);
                            } else {
                                window.location.href = url;
                            }
                            return;
                        }
                        if (this.reqContext) {
                            setRedirect(this.reqContext, 307, `/${res.dealerSlug}`);
                        } else {
                            this.router.navigate(['/', res.dealerSlug]);
                        }
                    } catch {
                        /* Empty */
                    }
                }),
            );
        },
        { dispatch: false },
    );

    getJwtFromPartnerWeb$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(actions.getJwtFromPartnerWeb),
            switchMap(() =>
                from(this.userApi.getPartnerWebJwt()).pipe(
                    map((data) => {
                        if (!data || !data.jwt || data.jwt === null) {
                            return actions.getJwtFromPartnerWebFailure({ error: 'No Jwt from PartnerWeb' });
                        }
                        this.cookieService.set(DEALER_AUTH_COOKIE_NAME, data.jwt, 1);
                        return actions.getJwtFromPartnerWebSuccess({ jwt: data.jwt });
                    }),
                    catchError((error) => of(actions.getJwtFromPartnerWebFailure({ error }))),
                ),
            ),
        );
    });

    redirectToImprintPage$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(actions.getJwtFromPartnerWebSuccess),
                switchMap(() =>
                    from(this.userApi.getDealerSlug()).pipe(
                        tap((res) => {
                            if (res.dealerSlug) {
                                this.router.navigate([getImprintUrl(res.dealerSlug)]);
                            } else {
                                this.router.navigate(['/']);
                            }
                        }),
                        catchError(() => this.router.navigate(['/'])),
                    ),
                ),
            );
        },
        { dispatch: false },
    );

    handleJwtFailure$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(actions.getJwtFromPartnerWebFailure),
                switchMap(() => this.router.navigate(['/'])),
            );
        },
        { dispatch: false },
    );
}
